import { apiClient } from '@/lib/axios';

/** Max file size for leave attachments: 10 MB */
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

/** Allowed MIME types for leave attachments (BA §13.6) */
export const ALLOWED_ATTACHMENT_TYPES: Record<string, string> = {
  'application/pdf': '.pdf',
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
};

export const ALLOWED_EXTENSIONS = Object.values(ALLOWED_ATTACHMENT_TYPES).join(', ');
export const ACCEPT_STRING = Object.keys(ALLOWED_ATTACHMENT_TYPES).join(',');

interface PresignResponse {
  uploadUrl: string;
  publicUrl: string;
}

/**
 * Get a presigned PUT URL from the server, then upload the file
 * directly to R2, returning the public URL.
 */
export async function uploadFile(file: File, folder: string): Promise<string> {
  // 1. Validate client-side
  if (!ALLOWED_ATTACHMENT_TYPES[file.type]) {
    throw new Error(`File type not allowed. Accepted: ${ALLOWED_EXTENSIONS}`);
  }
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`File too large. Maximum: ${MAX_FILE_SIZE / 1024 / 1024}MB`);
  }

  // 2. Get presigned URL
  const res = await apiClient.post<{ success: boolean; data: PresignResponse }>('/files/presign', {
    folder,
    contentType: file.type,
    fileName: file.name,
  });
  const { uploadUrl, publicUrl } = res.data.data;

  // 3. Upload directly to R2
  await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });

  return publicUrl;
}
